import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useHistory,
} from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import { fetchUser, bootstrapFromSso } from "slices/user";
import { parseJwtPayload } from "utils/jwt";
import { Box, CircularProgress } from "@mui/material";

import "./app.scss";

import Auth from "./pages/Auth";
import Home from "pages/Home";
import PJPView from "pages/PJPView";
import VisistsList from "pages/Visits";

import MasterApp from "./master";
import MasterDashboard from "master/dashboard/MasterDashboard";
import AttendanceView from "pages/Attendance";
import DailyVisit from "pages/Daily Visit";
import TimeSpent from "pages/TimeSpent";
import Sync from "pages/Sync";
import Onboard from "pages/onboard";
import UpdateLoaction from "pages/Update Dealer Location";
import SwitchBeat from "pages/Switch Beat";
import Upload from "pages/Upload";
import AddDocument from "pages/Add Document";
import VisitDownload from "pages/Visits Data Download";
import EditDocument from "pages/Edit Document";
import ProspectusVisits from "pages/Prospectus Visits";
import Tasks from "pages/Tasks";
import Leads from "pages/Leads";
import Dashboard from "pages/Dashboard";
import VisitStats from "pages/Visits Stats";
import TaskStats from "pages/Task Stats";
import { WearNature } from "pages/SE_Journey";
import DealerChurn from "pages/DealerChurn";
import Actyvai from "pages/Actyvai"
import Agreement from "pages/Actyvai/Agreement"
import DealerForm from "pages/Actyvai/DealerForm"
import Holiday from "pages/Holiday";
import DealerDirectory from "pages/DealerDirectory";
import ReimbursementCategory from "pages/ReimbursementCategory";
import Reimbursement from "pages/Reimbursements";
import AttendanceData from "pages/Attendance Data";
import ExpenseDashboard from "pages/Expense Dashboard";
import SpendLimit from "pages/Spend limit";
import SchemeScanner from "pages/Scheme Scanner";
import SurevyReconcilation from "pages/Reconcilation";
import MarketResearch from "pages/Market Research";
import DealerHelpDesk from "pages/Dealer Help Desk";
import DealerOnboardForm from "pages/DealerOnboard";
import DealerOnboardPreview from "pages/DealerOnboard/Preview";
import SEDepot from "pages/SE Depot";
import SEReports from "pages/SE Reports";
import SEAnalytics from "pages/SE Analytics";
import SEAnalyticsVisits from "pages/SE Analytics/VisitsData";
import SEAnalyticsDailyCalls from "pages/SE Analytics/DailyCallsData";
import RolesPage from "pages/Roles";
import SERolesPage from "pages/SE Roles";
import { firstGrantedLink, grantedResources, SUBADMIN_ROLE } from "config/dashboardPages";

import AdminOTR from "pages/AdminOTR";
import DealerMap from "pages/DealerMap";
import Documentation from "pages/Documentation";
import BulkComplianceInspector from "pages/Bulk Compliance Inspector";
import AppMaintenance from "pages/AppMaintenance";
import ForceUpdate from "pages/ForceUpdate";
// import SchemeScanner from "pages/SchemeScanner";

function finishSsoLogin(dispatch, history, profilePayload) {
  const uid = profilePayload?._id ?? profilePayload?.id;
  if (uid) {
    console.info("[SSO] session from /user — redirecting to /dashboard", {
      uid: String(uid),
    });
    history.replace("/dashboard");
    return;
  }

  const token = localStorage.getItem("token");
  const claims = parseJwtPayload(token);
  const jid = claims?._id ?? claims?.sub;
  if (!jid) {
    console.warn(
      "[SSO] no user id in /user response or JWT; staying on login",
      {
        profileKeys:
          profilePayload && typeof profilePayload === "object"
            ? Object.keys(profilePayload).slice(0, 20)
            : [],
      }
    );
    return;
  }

  const SAP_ID =
    claims.nameID ?? claims.nameIID ?? claims.SAP_ID ?? claims.sap_id;

  dispatch(
    bootstrapFromSso({
      _id: jid,
      SAP_ID,
    })
  );
  console.info("[SSO] bootstrapped from JWT; hydrating profile; /dashboard", {
    _id: String(jid),
    SAP_ID,
  });
  dispatch(fetchUser({ preserveUser: true }));
  history.replace("/dashboard");
}

// Shown while the token is being validated so we never flash the login page
const AuthBootstrapping = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
    }}
  >
    <CircularProgress sx={{ color: "#fdbc00" }} />
  </Box>
);

/**
 * A protected route that renders a spinner while bootstrapping,
 * the login page when unauthenticated, and the real component otherwise.
 */
const PrivateRoute = ({ children, ...rest }) => {
  const { user, loading } = useSelector((state) => state.user);
  return (
    <Route {...rest}>
      {loading ? <AuthBootstrapping /> : !user?._id ? <Auth /> : children}
    </Route>
  );
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (token) {
      console.info("[SSO] ?token= present — saving, stripping query, GET /user");
      localStorage.setItem("token", token);
      const cleanPath =
        `${window.location.pathname}${window.location.hash}` || "/";
      window.history.replaceState({}, document.title, cleanPath);

      dispatch(fetchUser())
        .unwrap()
        .then((payload) => {
          finishSsoLogin(dispatch, history, payload);
        })
        .catch((err) => {
          console.error("[SSO] fetchUser after SAML token failed", err);
          finishSsoLogin(dispatch, history, null);
        });
      return;
    }

    dispatch(fetchUser());
  }, [dispatch, history]);

  return (
    <Switch>
      {/* Master routes — no auth guard */}
      <Route exact path="/master">
        <MasterApp />
      </Route>
      <Route exact path="/master/dashboard">
        <MasterDashboard />
      </Route>

      {/* Public agreement page — no auth guard */}
      <Route exact path="/dealer-agreement/documentId::documentId">
        <Agreement />
      </Route>

      {/* Home — role-aware redirect after auth */}
      <PrivateRoute exact path="/">
        {user?.role === "ZTM" ? (
          <Redirect to="/vists-download" />
        ) : user?.role === "ADMINOTR" ? (
          <Redirect to="/admin-otr" />
        ) : user?.role === "ADMINSE" ? (
          <Redirect to="/se-depot" />
        ) : user?.role === SUBADMIN_ROLE ? (
          // A sub-admin granted the Home page lands on it directly; otherwise
          // fall through to their first granted page. (Redirecting to "/" here
          // when Home is granted would loop, since firstGrantedLink returns "/".)
          grantedResources(user?.permissions).has("/") ? (
            <Home />
          ) : (
            <Redirect to={firstGrantedLink(user?.permissions) || "/no-access"} />
          )
        ) : (
          <Home />
        )}
      </PrivateRoute>

      {/* Protected routes */}
      <PrivateRoute exact path="/dashboard"><Dashboard /></PrivateRoute>
      <PrivateRoute path="/SO/:SAP_ID/PJP"><PJPView /></PrivateRoute>
      <PrivateRoute exact path="/sync"><Sync /></PrivateRoute>
      <PrivateRoute exact path="/onboard"><Onboard /></PrivateRoute>
      <PrivateRoute exact path="/visit-stats"><VisitStats /></PrivateRoute>
      <PrivateRoute exact path="/wear-nature"><WearNature /></PrivateRoute>
      <PrivateRoute exact path="/task-stats"><TaskStats /></PrivateRoute>
      <PrivateRoute exact path="/update-location"><UpdateLoaction /></PrivateRoute>
      <PrivateRoute exact path="/switch-beat"><SwitchBeat /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/visits"><VisistsList /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/attendance"><AttendanceView /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/daily"><DailyVisit /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/daily/timespent"><TimeSpent /></PrivateRoute>
      <PrivateRoute exact path="/upload-documents"><Upload /></PrivateRoute>
      <PrivateRoute exact path="/add-documents"><AddDocument /></PrivateRoute>
      <PrivateRoute exact path="/vists-download"><VisitDownload /></PrivateRoute>
      <PrivateRoute exact path="/upload-documents/edit/:id"><EditDocument /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/prospectus/visits"><ProspectusVisits /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/tasks"><Tasks /></PrivateRoute>
      <PrivateRoute exact path="/SO/:SAP_ID/leads"><Leads /></PrivateRoute>
      <PrivateRoute exact path="/dealer-churn"><DealerChurn /></PrivateRoute>
      <PrivateRoute exact path="/actyv-ai"><Actyvai /></PrivateRoute>
      <PrivateRoute exact path="/actyv-ai/dealer-form"><DealerOnboardForm /></PrivateRoute>
      <PrivateRoute exact path="/actyv-ai/dealer-form/preview"><DealerOnboardPreview /></PrivateRoute>
      <PrivateRoute exact path="/dealer-help-desk"><DealerHelpDesk /></PrivateRoute>
      <PrivateRoute exact path="/holiday"><Holiday /></PrivateRoute>
      <PrivateRoute exact path="/dealer-directory"><DealerDirectory /></PrivateRoute>
      <PrivateRoute exact path="/reimbursement-category"><ReimbursementCategory /></PrivateRoute>
      <PrivateRoute exact path="/reimbursement"><Reimbursement /></PrivateRoute>
      <PrivateRoute exact path="/attendance-data"><AttendanceData /></PrivateRoute>
      <PrivateRoute exact path="/expense-dash"><ExpenseDashboard /></PrivateRoute>
      <PrivateRoute exact path="/spend-limit"><SpendLimit /></PrivateRoute>
      <PrivateRoute exact path="/survey"><SchemeScanner /></PrivateRoute>
      <PrivateRoute exact path="/survey-reconciliation"><SurevyReconcilation /></PrivateRoute>
      <PrivateRoute exact path="/market-research"><MarketResearch /></PrivateRoute>
      <PrivateRoute exact path="/se-depot"><SEDepot /></PrivateRoute>
      <PrivateRoute exact path="/se-reports"><SEReports /></PrivateRoute>
      <PrivateRoute exact path="/roles"><RolesPage /></PrivateRoute>
      <PrivateRoute exact path="/se-roles"><SERolesPage /></PrivateRoute>
      <PrivateRoute exact path="/se-analytics"><SEAnalytics /></PrivateRoute>
      <PrivateRoute exact path="/se-analytics/visits"><SEAnalyticsVisits /></PrivateRoute>
      <PrivateRoute exact path="/se-analytics/daily-calls"><SEAnalyticsDailyCalls /></PrivateRoute>

      <PrivateRoute exact path="/admin-otr"><AdminOTR /></PrivateRoute>
      <PrivateRoute exact path="/dealer-map"><DealerMap /></PrivateRoute>
      <PrivateRoute exact path="/documentation"><Documentation /></PrivateRoute>
      <PrivateRoute exact path="/compliance-inspect"><BulkComplianceInspector /></PrivateRoute>
      <PrivateRoute exact path="/app-maintenance"><AppMaintenance /></PrivateRoute>
      <PrivateRoute exact path="/force-update"><ForceUpdate /></PrivateRoute>
    </Switch>
  );
};

const App = () => (
  <div className="app">
    <Router>
      <AppRoutes />
    </Router>
  </div>
);

export default App;
